"use strict";

import { getDurationStatus } from "../../utils/generalFunctions.js";

export const getSpeakerDisplayName = (characters = [], speaker) => {
    const safeCharacters = Array.isArray(characters) ? characters : [];
    const result = safeCharacters.find((char) => char.speaker === speaker);

    return result?.displayName || speaker;
};

export const findTranslationByLanguageCode = (translations = [], languageCode) => {
    const safeTranslations = Array.isArray(translations) ? translations : [];

    return safeTranslations.find(
        (translation) => translation?.languageCode === languageCode,
    );
};

export const getGeneratedAudioOptionValue = (audio) =>
    audio?.objectPath || audio?.url || "";

export const getSelectedGeneratedAudioValue = (translation) =>
    translation?.translatedAudioPath || translation?.translatedAudio || "";

export const shouldExtendPreviewTranslatedAudio = ({
    dialogueStart,
    dialogueEnd,
    audioDuration,
    targetAudioTime,
}) => {
    const start = Number(dialogueStart);
    const end = Number(dialogueEnd);
    const duration = Number(audioDuration);
    const audioTime = Number(targetAudioTime);
    const timelineDuration = end - start;

    if (
        !Number.isFinite(start) ||
        !Number.isFinite(end) ||
        !Number.isFinite(duration) ||
        !Number.isFinite(audioTime) ||
        timelineDuration <= 0 ||
        duration <= timelineDuration
    ) {
        return false;
    }

    return audioTime >= 0 && audioTime < duration;
};

export const createEmptyTranslation = (languageCode) => ({
    languageCode,
    translatedText: "",
    translatedAudioList: [],
    translatedAudio: "",
    translatedAudioRating: "",
    translatedAudioNote: null,
    translatedAudioPath: null,
    translatedRecording: "",
});

export const ensureProjectFinalLanguageTranslations = (
    translations = [],
    finalLanguageCodes = [],
) => {
    const safeTranslations = Array.isArray(translations) ? translations : [];
    const updatedTranslations = [...safeTranslations];
    const existingLanguageCodes = safeTranslations.map(
        (translation) => translation?.languageCode,
    );

    (finalLanguageCodes || []).forEach((languageCode) => {
        if (!existingLanguageCodes.includes(languageCode)) {
            updatedTranslations.push(createEmptyTranslation(languageCode));
        }
    });

    return updatedTranslations;
};

export const getDefaultVoiceIdForEmotion = (
    characters = [],
    speaker,
    originalAudioEmotion,
) => {
    const safeCharacters = Array.isArray(characters) ? characters : [];
    const character = safeCharacters.find((char) => char.speaker === speaker);
    const characterVoice = character?.voices?.find(
        (voice) => voice.emotion === originalAudioEmotion,
    );

    return characterVoice?.voice_id || "";
};

// The character a line is spoken in: the first with the line's speaker key.
// Keys are unique per project, but lists saved before that was checked can
// repeat one, and the backend resolves those to the first match too.
export const getLineCharacter = (characters, speaker) =>
  (Array.isArray(characters)
    ? characters.find((character) => character?.speaker === speaker)
    : undefined) ?? null;

// A line can take a bulk speed adjustment when the Speed dropdown would be
// enabled for it: it has translated audio, keeps no original text and is not
// subtitle-only. voice-generation-service's speed job checks the same.
export const isBulkSpeedSelectable = (dialogue, languageCode, subtitleOnlyMap = {}) => {
    if (!dialogue || dialogue.keepOriginalText) {
        return false;
    }
    if ((subtitleOnlyMap[dialogue.dbid] ?? dialogue.subtitleOnly) === true) {
        return false;
    }
    const translation = findTranslationByLanguageCode(dialogue.translations, languageCode);
    return Boolean(translation?.translatedAudio || translation?.translatedAudioPath);
};

// Whether the row shows "Make It Longer" or "Make It Shorter".
export const isDurationFlagged = (dialogue, languageCode) => {
    const translation = findTranslationByLanguageCode(dialogue?.translations, languageCode);
    const translatedLength =
        translation?.translatedAudioDuration ?? dialogue?.translatedAudioDurationTemp;
    const status = getDurationStatus(
        Number(dialogue?.end) - Number(dialogue?.start),
        translatedLength,
    );
    return status === "needsLonger" || status === "needsShorter";
};

export const getTranslationDurationMessage = (originalLength, translatedLength) => {
    const status = getDurationStatus(originalLength, translatedLength);

    if (status === "perfect") {
        return { label: "Perfect", legacyLabel: "Duration Perfect", color: "inherit" };
    }
    if (status === "needsLonger") {
        return { label: "Make It Longer", legacyLabel: "Make It Longer", color: "red" };
    }
    if (status === "needsShorter") {
        return { label: "Make It Shorter", legacyLabel: "Make It Shorter", color: "red" };
    }

    return null;
};

// Translated audio "overlaps" when it plays up to or past the start of the
// next line. Keep in sync with the backend's translation-overlap filter
// (audio_dashboard_backend/utils/dialogueFilters.js).
export const isTranslatedAudioOverlapping = (start, translatedDuration, nextStart) => {
    const toNumber = (value) =>
        value === null || value === undefined || value === "" ? NaN : Number(value);

    const startTime = toNumber(start);
    const duration = toNumber(translatedDuration);
    const nextStartTime = toNumber(nextStart);

    if (
        !Number.isFinite(startTime) ||
        !Number.isFinite(duration) ||
        !Number.isFinite(nextStartTime)
    ) {
        return false;
    }

    return startTime + duration >= nextStartTime;
};

export const getRecommendedSpeedOption = (originalLength, translatedLength) => {
    const status = getDurationStatus(originalLength, translatedLength);

    if (status !== "needsLonger" && status !== "needsShorter") {
        return null;
    }

    const speedOptions = [0.8, 0.9, 1.0, 1.1, 1.2];
    const rankedOptions = speedOptions
        .map((speedOption) => {
            const adjustedDuration = translatedLength / speedOption;
            const adjustedStatus = getDurationStatus(originalLength, adjustedDuration);
            const absoluteDurationDifference = Math.abs(
                adjustedDuration - originalLength,
            );
            const stillWarning =
                adjustedStatus === "needsLonger" || adjustedStatus === "needsShorter";

            return {
                speedOption,
                adjustedDuration,
                absoluteDurationDifference,
                stillWarning,
            };
        })
        .sort((a, b) => {
            if (a.stillWarning !== b.stillWarning) {
                return a.stillWarning ? 1 : -1;
            }
            if (a.absoluteDurationDifference !== b.absoluteDurationDifference) {
                return a.absoluteDurationDifference - b.absoluteDurationDifference;
            }
            return Math.abs(a.speedOption - 1.0) - Math.abs(b.speedOption - 1.0);
        });

    return rankedOptions[0]?.speedOption ?? null;
};

export const findDialogueInTimeRange = (dialogues = [], time) => {
    const safeDialogues = Array.isArray(dialogues) ? dialogues : [];

    return safeDialogues.find(
        (dialogue) => time >= dialogue.start && time < dialogue.end,
    );
};

export const findLatestDialogueIndexInTimeRange = (
    dialogues = [],
    time,
    isPlayable = () => true,
) => {
    if (!Array.isArray(dialogues) || !Number.isFinite(Number(time))) {
        return null;
    }

    return dialogues.reduce((activeIndex, dialogue, index) => {
        const start = Number(dialogue?.start);
        const end = Number(dialogue?.end);
        const isInRange =
            Number.isFinite(start) &&
            Number.isFinite(end) &&
            Number(time) >= start &&
            Number(time) < end;

        if (!isInRange || !isPlayable(dialogue)) {
            return activeIndex;
        }

        if (
            activeIndex === null ||
            Number(dialogues[activeIndex]?.start) < start
        ) {
            return index;
        }

        return activeIndex;
    }, null);
};

export const getDialoguePageEndTime = (dialogues = []) => {
    if (!Array.isArray(dialogues)) {
        return null;
    }

    const validEndTimes = dialogues
        .map((dialogue) => Number(dialogue?.end))
        .filter((endTime) => Number.isFinite(endTime));

    return validEndTimes.length > 0 ? Math.max(...validEndTimes) : null;
};
